/* Lusedagbok-bestillinger: trykkekøen.
 *
 * For ansatte, på Operators-siden, fordi det er der kundene og anleggene
 * deres bor. Bestillingen kommer også som e-post til ops med trykkfilen
 * vedlagt; dette er stedet å se hva som venter og flytte det videre, slik at
 * e-posten ikke blir det eneste minnet om at noe er sendt.
 *
 * Skjules helt når køen er tom og ingenting er bestilt, eller når receiveren
 * er eldre enn Lusedagboka og svarer 404 -- et kort som bare sier «ingenting»
 * er et kort alle lærer seg å scrolle forbi.
 */
import { useState } from 'react'
import {
  liceBookPdf, setLiceBookOrderStatus,
  type LiceBookOrderRow, type LiceBookStatus,
} from '../api'
import { useApi } from '../useApi'
import { ErrorBox } from './bits'

const STATUS: Record<LiceBookStatus, string> = {
  received: 'Mottatt',
  sent_to_print: 'Sendt til trykk',
  shipped: 'Sendt kunden',
  cancelled: 'Kansellert',
}

const NESTE: Partial<Record<LiceBookStatus, LiceBookStatus>> = {
  received: 'sent_to_print',
  sent_to_print: 'shipped',
}

export function LiceBookOrders({ canWrite }: { canWrite: boolean }) {
  const q = useApi<{ orders: LiceBookOrderRow[] }>('/v1/lice-book-orders')
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<unknown>(null)

  if (q.error?.status === 404 || q.cold) return null
  if (q.error) return <ErrorBox error={q.error} />
  const orders = q.data?.orders ?? []
  const open = orders.filter((o) => o.status === 'received' || o.status === 'sent_to_print')
  if (!orders.length) return null

  async function move(o: LiceBookOrderRow, to: LiceBookStatus) {
    setBusy(o.order_id); setErr(null)
    try {
      await setLiceBookOrderStatus(o.order_id, to)
      await q.reloadAsync()
    } catch (e) {
      setErr(e)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="card flush">
      <div className="card-head">
        <h2>Lusedagbok-bestillinger</h2>
        <span className="muted">{open.length ? `${open.length} venter` : 'Ingen venter'}</span>
      </div>
      {err instanceof Error && <ErrorBox error={err} />}
      <div className="scroll-x">
        <table className="tbl">
          <thead>
            <tr><th>Bestilt</th><th>Anlegg</th><th className="num">Antall</th>
                <th>Sendes til</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {orders.slice(0, 30).map((o) => {
              const next = NESTE[o.status]
              return (
                <tr key={o.order_id}>
                  <td data-l="Bestilt">{new Date(o.created_at).toLocaleDateString('nb-NO')}</td>
                  <td data-l="Anlegg">
                    <strong>{o.site_name}</strong>
                    <span className="muted"> · {o.locality_no ?? '–'}{o.company ? ` · ${o.company}` : ''}</span>
                  </td>
                  <td data-l="Antall" className="num">{o.qty}</td>
                  <td data-l="Sendes til">{o.recipient}, {o.address}, {o.postcode} {o.city}
                    {o.phone && <span className="muted"> · {o.phone}</span>}</td>
                  <td data-l="Status">{STATUS[o.status] ?? o.status}</td>
                  <td className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    <a className="btn sm ghost" target="_blank" rel="noopener"
                      href={liceBookPdf(o.site_id, o.book_id)}>PDF</a>
                    {canWrite && next && (
                      <button className="btn sm" disabled={busy === o.order_id}
                        onClick={() => void move(o, next)}>
                        {STATUS[next]}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
